import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  ServerMessage,
  VaultCandidate,
  VaultCandidatesResponse,
  VaultConfigResponse,
  VaultFolderDialogResponse,
  VaultEvent,
  VaultGraph
} from '../../shared/types';
import { createDemoVaultEvents, createDemoVaultGraph, createTrailerVaultEvents, createTrailerVaultGraph } from '../demo/demo-vault';
import { fetchJson, shouldUseStaticDemo, websocketEndpoint } from '../lib/api-client';
import { capturePresetFromLocation } from '../lib/capture-mode';

type ConnectionState = 'connecting' | 'live' | 'offline' | 'demo';

const liveWindowMs = 1000 * 60 * 15;

function recentLivePathMap(nextEvents: VaultEvent[], now = Date.now()): Record<string, number> {
  const cutoff = now - liveWindowMs;
  return Object.fromEntries(
    nextEvents
      .filter((event) => event.path && event.path !== '.' && Date.parse(event.at) >= cutoff)
      .map((event) => [event.path, Date.parse(event.at)])
  );
}

export function useVaultKosmos() {
  const staticDemo = useMemo(() => shouldUseStaticDemo(), []);
  const capturePreset = useMemo(() => capturePresetFromLocation(), []);
  const [graph, setGraph] = useState<VaultGraph | null>(null);
  const [events, setEvents] = useState<VaultEvent[]>([]);
  const [connection, setConnection] = useState<ConnectionState>('connecting');
  const [configState, setConfigState] = useState<VaultConfigResponse | null>(null);
  const [vaultCandidates, setVaultCandidates] = useState<VaultCandidate[]>([]);
  const [configError, setConfigError] = useState<string | undefined>();
  const [configSaving, setConfigSaving] = useState(false);
  const [vaultDialogOpen, setVaultDialogOpen] = useState(false);
  const [livePaths, setLivePaths] = useState<Record<string, number>>({});
  const reconnectTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    let disposed = false;

    function activateDemo() {
      const now = capturePreset?.fixedNow ?? Date.now();
      const useTrailerVault = capturePreset?.id === 'trailer';
      const demoEvents = useTrailerVault ? createTrailerVaultEvents(now) : createDemoVaultEvents(now);
      setGraph(useTrailerVault ? createTrailerVaultGraph(now) : createDemoVaultGraph(now));
      setEvents(demoEvents);
      setLivePaths(recentLivePathMap(demoEvents, now));
      setConnection('demo');
      setConfigError(undefined);
      const vaultPath = useTrailerVault ? 'Vault Kosmos Trailer Vault' : 'GitHub Pages Demo Vault';
      setConfigState({
        config: {
          vaultPath,
          host: '127.0.0.1',
          port: 8787,
          historyLimit: 200,
          watch: {
            debounceMs: 120,
            awaitWriteFinishMs: 350
          }
        },
        status: {
          vaultPath,
          valid: true,
          exists: true,
          isDirectory: true,
          hasObsidianDir: true,
          message: 'Static demo vault loaded.'
        }
      });
    }

    async function bootstrap() {
      if (staticDemo) {
        activateDemo();
        return;
      }

      const [graphResult, eventsResult] = await Promise.all([
        fetchJson<VaultGraph>('/api/graph'),
        fetchJson<VaultEvent[]>('/api/events?limit=200'),
        refreshConfig(),
        refreshVaultCandidates()
      ]);
      if (!disposed) {
        const nextEvents = eventsResult.data;
        setGraph(graphResult.data);
        setEvents(nextEvents);
        setLivePaths(recentLivePathMap(nextEvents));
      }
    }

    void bootstrap().catch((error) => {
      setConnection('offline');
      setConfigError(String(error));
    });
    return () => {
      disposed = true;
    };
  }, [capturePreset, staticDemo]);

  useEffect(() => {
    if (staticDemo) return;

    let socket: WebSocket | undefined;
    let disposed = false;

    function connect() {
      if (disposed) return;
      setConnection('connecting');
      socket = new WebSocket(websocketEndpoint());

      socket.addEventListener('open', () => setConnection('live'));
      socket.addEventListener('close', () => {
        setConnection('offline');
        if (!disposed) {
          reconnectTimer.current = window.setTimeout(connect, 1500);
        }
      });
      socket.addEventListener('error', () => setConnection('offline'));
      socket.addEventListener('message', (message) => {
        const payload = JSON.parse(String(message.data)) as ServerMessage;
        handleMessage(payload);
      });
    }

    function handleMessage(message: ServerMessage) {
      if (message.type === 'graph:init') {
        setGraph(message.graph);
        setEvents(message.events);
        setLivePaths(recentLivePathMap(message.events));
        return;
      }

      if (message.type === 'graph:patch') {
        setGraph(message.graph);
        registerLiveEvent(message.event, false);
        return;
      }

      if (message.type.startsWith('file:')) {
        registerLiveEvent(message.event, true);
      }

      if (message.type === 'watcher:ready' || message.type === 'watcher:error') {
        setEvents((current) => [...current.slice(-199), message.event]);
      }
    }

    function registerLiveEvent(event: VaultEvent, append: boolean) {
      if (event.path && event.path !== '.') {
        setLivePaths((current) => ({
          ...current,
          [event.path]: Date.parse(event.at)
        }));
      }
      if (append) setEvents((current) => [...current.slice(-199), event]);
    }

    connect();
    return () => {
      disposed = true;
      if (reconnectTimer.current) window.clearTimeout(reconnectTimer.current);
      socket?.close();
    };
  }, [staticDemo]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      const cutoff = (capturePreset?.fixedNow ?? Date.now()) - liveWindowMs;
      setLivePaths((current) =>
        Object.fromEntries(Object.entries(current).filter(([, at]) => at >= cutoff))
      );
    }, 1500);

    return () => window.clearInterval(interval);
  }, [capturePreset]);

  const liveNodeIds = useMemo(
    () => new Set(Object.keys(livePaths).map((path) => `file:${path}`)),
    [livePaths]
  );

  async function refreshConfig(): Promise<VaultConfigResponse | undefined> {
    const { response, data: nextConfig } = await fetchJson<VaultConfigResponse>('/api/config');
    if (!response.ok) throw new Error(`Config konnte nicht geladen werden (${response.status}).`);
    setConfigState(nextConfig);
    return nextConfig;
  }

  async function updateVaultPath(vaultPath: string): Promise<boolean> {
    if (staticDemo) {
      setConfigError('Die GitHub-Pages-Demo kann keinen lokalen Vault öffnen. Starte dafür den lokalen Connector.');
      return false;
    }

    setConfigSaving(true);
    setConfigError(undefined);
    try {
      const { response, data: payload } = await fetchJson<VaultConfigResponse & { error?: string }>('/api/config', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ vaultPath })
      });
      setConfigState(payload);
      if (!response.ok) {
        setConfigError(payload.status?.message ?? payload.error ?? 'Vault-Pfad ist ungültig.');
        return false;
      }
      await refreshVaultCandidates();
      return true;
    } catch (error) {
      setConfigError(String(error));
      return false;
    } finally {
      setConfigSaving(false);
    }
  }

  async function chooseVaultWithDialog(): Promise<boolean> {
    if (staticDemo) {
      setConfigError('Die GitHub-Pages-Demo nutzt einen statischen Demo-Vault. Lokale Vaults brauchen den lokalen Connector.');
      return false;
    }

    setVaultDialogOpen(true);
    setConfigError(undefined);
    try {
      const { response, data: payload } = await fetchJson<VaultFolderDialogResponse>('/api/vault-dialog', { method: 'POST' });

      if (!response.ok || payload.error) {
        setConfigError(payload.error ?? `Ordnerdialog konnte nicht geöffnet werden (${response.status}).`);
        return false;
      }

      if (payload.cancelled) return false;

      if (!payload.path) {
        setConfigError('Kein Vault-Pfad ausgewählt.');
        return false;
      }

      if (payload.status && !payload.status.valid) {
        setConfigState((current) => (current ? { ...current, status: payload.status! } : current));
        setConfigError(payload.status.message ?? 'Vault-Pfad ist ungültig.');
        return false;
      }

      return updateVaultPath(payload.path);
    } catch (error) {
      setConfigError(String(error));
      return false;
    } finally {
      setVaultDialogOpen(false);
    }
  }

  async function refreshVaultCandidates(): Promise<VaultCandidate[]> {
    try {
      const { response, data: payload } = await fetchJson<VaultCandidatesResponse>('/api/vault-candidates');
      if (!response.ok) return [];
      setVaultCandidates(payload.candidates);
      return payload.candidates;
    } catch {
      return [];
    }
  }

  return {
    graph,
    events,
    connection,
    configState,
    vaultCandidates,
    configError,
    configSaving,
    vaultDialogOpen,
    livePaths,
    liveNodeIds,
    demoMode: staticDemo,
    updateVaultPath,
    chooseVaultWithDialog,
    refreshConfig
  };
}
